/**
 * Web Push helpers for Novu-backed PWA notifications via Firebase Cloud Messaging.
 *
 * @novu/js v3 (Inbox) has no Web Push subscribe API — device tokens must be
 * registered via the auth backend (secret-key Novu credentials.append).
 *
 * Outside this repo:
 * - Enable FCM Push in the Novu dashboard and configure Firebase service account JSON
 *   (NOT the legacy server key; Novu uses the service account credentials).
 * - Set Firebase web config env vars (API key, project ID, sender ID, app ID) in .env.production.
 * - Set VITE_FIREBASE_VAPID_KEY to the web push VAPID key from Firebase Console.
 *
 * iOS: Push only works after the PWA is added to the Home Screen (standalone).
 * Unsupported browsers: all helpers no-op / return null.
 */

import { getToken, isSupported } from 'firebase/messaging'
import { getFirebaseMessaging } from './firebase'
import { isInstalledApp } from './installedApp'

const OPT_IN_STORAGE_KEY = 'escribonauta_web_push_opt_in_fcm'
const LAST_REGISTERED_TOKEN_KEY = 'escribonauta_fcm_last_token'

export type WebPushSupportStatus =
  | 'supported'
  | 'unsupported'
  | 'ios-needs-install'
  | 'missing-config'

function isIosDevice(): boolean {
  const ua = window.navigator.userAgent
  return (
    /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function getFirebaseVapidKey(): string {
  return (import.meta.env.VITE_FIREBASE_VAPID_KEY || '').trim()
}

export async function getWebPushSupportStatus(): Promise<WebPushSupportStatus> {
  if (typeof window === 'undefined') return 'unsupported'
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported'
  }

  try {
    const fcmSupported = await isSupported()
    if (!fcmSupported) return 'unsupported'
  } catch {
    return 'unsupported'
  }

  // iOS Safari only exposes reliable Web Push for installed PWAs.
  if (isIosDevice() && !isInstalledApp()) {
    return 'ios-needs-install'
  }
  const messaging = getFirebaseMessaging()
  const vapidKey = getFirebaseVapidKey()
  if (!messaging || !vapidKey) {
    return 'missing-config'
  }
  return 'supported'
}


export function readWebPushOptIn(): boolean {
  try {
    return localStorage.getItem(OPT_IN_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function writeWebPushOptIn(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(OPT_IN_STORAGE_KEY, '1')
    } else {
      localStorage.removeItem(OPT_IN_STORAGE_KEY)
      localStorage.removeItem(LAST_REGISTERED_TOKEN_KEY)
    }
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function getLastRegisteredToken(): string | null {
  try {
    return localStorage.getItem(LAST_REGISTERED_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setLastRegisteredToken(token: string): void {
  try {
    localStorage.setItem(LAST_REGISTERED_TOKEN_KEY, token)
  } catch {
    // Ignore quota / private-mode failures.
  }
}

/**
 * Waits for service worker to be ready with a timeout.
 * If SW never installs (e.g., Firebase scripts failed), returns null after timeout.
 */
async function getServiceWorkerWithTimeout(timeoutMs: number = 10000): Promise<ServiceWorkerRegistration | null> {
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ])
    return registration
  } catch {
    return null
  }
}

/**
 * Obtains the current FCM registration token from Firebase Messaging.
 * This may create or renew a token if needed (not a read-only peek).
 * Returns null if Firebase is not configured or permission not granted.
 */
export async function getFcmToken(): Promise<string | null> {
  const status = await getWebPushSupportStatus()
  if (status === 'unsupported' || status === 'missing-config') return null

  if (Notification.permission !== 'granted') return null

  try {
    const messaging = getFirebaseMessaging()
    if (!messaging) return null

    const vapidKey = getFirebaseVapidKey()
    if (!vapidKey) return null

    const registration = await getServiceWorkerWithTimeout()
    if (!registration) {
      console.warn('[FCM] Service worker not ready after timeout')
      return null
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    })

    return token || null
  } catch (err) {
    console.error('[FCM] Failed to get token:', err)
    return null
  }
}

/**
 * Requests notification permission (must run from a user gesture) and obtains
 * an FCM registration token from Firebase Messaging.
 * Returns the FCM token string for registration with Novu backend.
 */
export async function subscribeFcmWebPush(): Promise<string | null> {
  const status = await getWebPushSupportStatus()
  if (status !== 'supported') return null

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return null

  return getFcmToken()
}

export { getServiceWorkerWithTimeout }
